import { CHART_TYPES } from '../../constants/chartTypes';
import { HISTOGRAM_LABEL } from '../../constants/utils';
import {
  IDisplayedMeasure,
  ILineMetaNewExpression,
  IParsedChartDataRecord,
} from '../../interfaces';
import { IChartLine } from '../useChartLines';
import { assertCondition, assertNotNil } from '../../../../utils/types';
import { getValueFromDataPoint } from '../../../../utils/redux/util';
import { IFunnelDataKeysPerLine } from '../../FunnelChart/dataKeysUtils';
import { isNil } from 'lodash';
import { isOverallSliceKey } from '../../utils';
import {
  IMeasureControl,
  QueryType,
  TimeUnit,
} from '../../../../utils/apollo/graphqlSchema';
import { getLineDisplayForSingleDateChart } from './utils';

interface IProps {
  data: IParsedChartDataRecord[];
  visibleLines: IChartLine[];
  singleDayLabel: string | null;
  linesMeta: ILineMetaNewExpression;
  ignoreOverall: boolean;
  chartType: CHART_TYPES;
  isHistogram?: boolean;
  dataKeysPerLine?: IFunnelDataKeysPerLine;
  measureControls: IMeasureControl[];
  displayedMeasures: IDisplayedMeasure[];
  queryType: QueryType.Funnel | QueryType.Expression;
  timeUnit?: TimeUnit;
}

export interface ISingleDateData extends IParsedChartDataRecord {
  [HISTOGRAM_LABEL]: string;
  value: number | null;
  name: string;
  label: string;
  percent: number | null;
  fill: string;
  stroke: string;
  color: string;
  xAxisIndex: number;
}

interface IResult {
  parsedData: ISingleDateData[];
  largestIndex: number;
}

export const SINGLE_DATE_DATA_KEYS = {
  valueKey: 'value',
  percentKey: 'percent',
  bounceValueKey: 'bounceValue',
  bouncePercentKey: 'bouncePercent',
  relativePercentKey: 'relativePercent',
  relativeBounceKey: 'relativeBounce',
  relativeBouncePercentKey: 'relativeBouncePercent',
  relativeScaledBounceKey: 'relativeScaledBounce',
  relativeScaledBouncePercentKey: 'relativeScaledBouncePercent',
};

const getDayData = ({
  isHistogram,
  data,
  singleDayLabel,
  isAllTime,
}: {
  isHistogram: boolean | undefined;
  data: IParsedChartDataRecord[];
  singleDayLabel: string | null;
  isAllTime: boolean;
}): {
  dayData: IParsedChartDataRecord;
  xAxisIndex: number;
} => {
  if (isAllTime && !isHistogram) {
    const xAxisIndex = 0;
    assertCondition(!!data[xAxisIndex]);
    return { dayData: data[xAxisIndex], xAxisIndex };
  }

  if (isHistogram) {
    const xAxisIndex = 0;
    return { dayData: data[xAxisIndex], xAxisIndex };
  }

  const xAxisIndex = data.findIndex((d) => d.date === singleDayLabel);
  assertCondition(xAxisIndex !== -1);
  return { dayData: data[xAxisIndex], xAxisIndex };
};

export default ({
  data,
  visibleLines,
  singleDayLabel,
  linesMeta,
  ignoreOverall = false,
  chartType,
  queryType,
  isHistogram,
  dataKeysPerLine,
  measureControls,
  displayedMeasures,
  timeUnit,
}: IProps): IResult => {
  const transformDataToSingleDate = (
    lines: IChartLine[],
  ): ISingleDateData[] => {
    // for histogram chart, the date label is a date range,
    // therefore we don't need to find the specific date.
    const { dayData, xAxisIndex } = getDayData({
      isHistogram,
      data,
      singleDayLabel,
      isAllTime: timeUnit === TimeUnit.AllTime,
    });
    assertNotNil(dayData);
    const filteredLines = ignoreOverall
      ? lines.filter(
          (line) => !isOverallSliceKey(linesMeta[line.name]?.sliceKey),
        )
      : lines;

    return filteredLines.map((line): ISingleDateData => {
      const lineKey = line.name;
      const lineMeta = linesMeta[lineKey];

      const lineDataKeys = dataKeysPerLine?.[lineKey];
      let values: Pick<ISingleDateData, 'value' | 'percent'> &
        Record<string, number | null>;
      if (!isNil(lineDataKeys)) {
        values = {
          value: getValueFromDataPoint(dayData, lineDataKeys.valueKey) ?? null,
          percent:
            getValueFromDataPoint(dayData, lineDataKeys.percentKey) ?? null,
          bounceValue:
            getValueFromDataPoint(dayData, lineDataKeys.bounceValueKey) ?? null,
          bouncePercent:
            getValueFromDataPoint(dayData, lineDataKeys.bouncePercentKey) ??
            null,
          relativePercent:
            getValueFromDataPoint(dayData, lineDataKeys.relativePercentKey) ??
            null,
          relativeBounce:
            getValueFromDataPoint(dayData, lineDataKeys.relativeBounceKey) ??
            null,
          relativeBouncePercent:
            getValueFromDataPoint(
              dayData,
              lineDataKeys.relativeBouncePercentKey,
            ) ?? null,
          relativeScaledBounce:
            getValueFromDataPoint(
              dayData,
              lineDataKeys.relativeScaledBounceKey,
            ) ?? null,
          relativeScaledBouncePercent:
            getValueFromDataPoint(
              dayData,
              lineDataKeys.relativeScaledBouncePercentKey,
            ) ?? null,
        };
      } else {
        values = {
          value: getValueFromDataPoint(dayData, lineKey) ?? null,
          percent:
            getValueFromDataPoint(dayData, lineMeta.percentLineKey) ?? null,
        };
      }

      const measureControl = measureControls.find(
        (mc) => mc.key === line.measureKey,
      );

      const updatedLineDisplay = getLineDisplayForSingleDateChart({
        line,
        linesMeta,
        displayedMeasures,
        isHistogram,
        queryType,
      });
      return {
        [HISTOGRAM_LABEL]: updatedLineDisplay,
        // to match color in tooltip
        name: line.name,
        label: updatedLineDisplay,
        // to display tooltips by using common function: builderLineValue
        [line.name]: values.value,
        // for pie chart, we need to set up color for each slice.
        fill: line.color,
        // consider the label color in PieChart if you change the default stroke from 'transparent'
        stroke: line.type === 'dashed' ? line.color : 'transparent',
        color: line.color,
        isLive: !!dayData.isLive,
        isResultKey: measureControl?.isResultKey ?? false,
        xAxisIndex,
        ...values,
      };
    });
  };

  const findLargeIndex = (parsedData: ISingleDateData[]) => {
    let currValue = 0;
    let resIndex = 0;

    // find the largest value and index in parsedData
    parsedData.forEach((d, index) => {
      if (!isNil(d.value) && d.value > currValue) {
        currValue = d.value;
        resIndex = index;
      }
    });
    return resIndex;
  };
  // if it's percent area chart, all the visible lines are using percent keys.
  // to display their value, we need to rename to the original keys.
  const renamedVisibleLines = [...visibleLines];
  if (chartType === CHART_TYPES.PERCENT_STACKED_AREA_CHART) {
    for (const line of renamedVisibleLines) {
      const parentKey = linesMeta[line.name].parentKey;
      if (parentKey) {
        line.name = parentKey;
      }
    }
  }
  const parsedData = transformDataToSingleDate(renamedVisibleLines);
  const largestIndex = findLargeIndex(parsedData);
  return { parsedData, largestIndex };
};
